import { useEffect, useState } from "react";
import { QrPrint } from "./QrPrint";
import { sanitizeRallyPoints, RALLY_DROP_SAY } from "@greg-chick/shared";
import {
  getRallyEvent,
  saveRallyEvent,
  type RallyPointDoc,
} from "../lib/contentApi";
import { ActionButton } from "./ActionButton";
import { useRegisterSave } from "../lib/saveBar";
import { Msg } from "./ui";

/**
 * 랠리 지점 편집(FR-RALLY) — 좌표·반경·인증방식·현장 QR 토큰.
 *
 * 왜 필요한가: 지점은 현장 운영의 핵심인데(실측 좌표·반경 조정·파트너 가게 교체)
 * 지금까지 content/rally.yaml 편집 → 빌드 → 재배포가 필요했다.
 * 서버가 좌표 범위와 **반경 10~500m** 를 강제한다 — 반경이 넓으면 현장에 가지 않아도
 * 인증돼 랠리가 무의미해지기 때문(부정방지).
 * QR 토큰은 **여기서만** 보인다(공개 API 에는 실리지 않음).
 */
export function RallyPointPanel() {
  const [points, setPoints] = useState<RallyPointDoc[]>([]);
  /** 불러온 그대로의 지점 — 저장할 때 「무엇이 빠지나」를 이것과 견준다 */
  const [loadedPoints, setLoadedPoints] = useState<RallyPointDoc[]>([]);
  const [tokens, setTokens] = useState<Record<string, string>>({});
  const [overridden, setOverridden] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  async function refresh() {
    const r = await getRallyEvent();
    setPoints(r.points ?? []);
    setLoadedPoints(r.points ?? []);
    setTokens(r.qrTokens ?? {});
    setOverridden(!!r.pointsOverridden);
    setLoaded(true);
  }
  useEffect(() => {
    refresh().catch(() => setMsg("엣지 서버에 연결하지 못했어요."));
  }, []);

  function update(i: number, patch: Partial<RallyPointDoc>) {
    setPoints((prev) => prev.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  }
  function add() {
    setPoints((prev) => [
      ...prev,
      { id: nextId(prev), title_ko: "", lat: 35.8347, lng: 129.219, radius_m: 80, verify: "geofence_ar" },
    ]);
  }
  function remove(i: number) {
    setPoints((prev) => prev.filter((_, idx) => idx !== i));
  }

  /**
   * 🔴 **서버가 무엇을 받아들이는지 미리 본다.**
   *
   * 예전엔 화면과 서버가 잣대를 따로 갖고 있었다 — 화면엔 21개가 보이는데 서버는 20개만
   * 저장하고, 반경 900m 를 500m 로 줄이면서 **아무 말도 안 했다.** 운영자는 저장한 줄 알고,
   * 아이 화면에서만 다르게 동작한다. 랠리는 **현장에 종이 QR 을 붙이는** 기능이라
   * 그 차이를 현장에 가서야 알게 된다.
   *
   * 이제 서버와 **같은 함수**로 미리 재서, 저장하기 전에 말한다.
   */
  const check = sanitizeRallyPoints(points);

  async function onSave() {
    /**
     * 🧾 **빠지는 지점이 있으면 묻는다** (2026-10-13). 서버는 지금 있는 지점만 센다(shared rallyProgress) —
     * 지우면 그 지점에서 받은 도장이 안 세지고, 현장에 붙인 QR 도 안 통한다. 묻지 않고 저장됐다.
     */
    const keep = new Set(check.points.map((p) => p.id));
    const gone = loadedPoints.filter((p) => !keep.has(p.id));
    if (gone.length) {
      const list = gone.map((p) => `• ${p.title_ko || p.id}`).join("\n");
      const ok = window.confirm(
        `저장하면 이 랠리 지점이 빠져요:\n\n${list}\n\n` +
          "이미 그곳에서 받은 도장은 더 세지 않고, 현장에 붙인 QR 도 더 안 통해요. 그래도 저장할까요?",
      );
      if (!ok) {
        setMsg(`⏸️ 안 저장했어요 — 빠지는 지점: ${gone.map((p) => p.title_ko || p.id).join(", ")}.`);
        throw new Error("빠지는 랠리 지점 확인에서 취소 — 안 저장했어요");
      }
    }
    await saveRallyEvent({}, { points: check.points, qrTokens: tokens });
    await refresh();
    const extra = [
      check.dropped.length ? `못 넣은 지점 ${check.dropped.length}곳` : "",
      check.changed.length ? `값이 바뀐 지점 ${check.changed.length}곳` : "",
    ].filter(Boolean);
    setMsg(
      `✅ 지점 ${check.points.length}곳을 저장했어요.` +
        (extra.length ? ` (${extra.join(" · ")} — 위 안내를 봐 주세요)` : ""),
    );
  }
  // 저장은 화면 아래 고정 바 하나로 모인다(이 탭엔 패널이 셋인데 버튼이 셋이었다).
  useRegisterSave({
    id: "rallypt",
    label: "랠리 지점",
    save: onSave,
    // 현장 토큰(tokens)은 **담지 않는다** — 비밀값을 브라우저 저장소에 남기지 않는다.
    draft: { data: () => points, restore: (d) => setPoints(d as RallyPointDoc[]) },
  });
  async function onReset() {
    // 빈 배열로 저장하면 서버가 "오버라이드 없음"으로 보고 원래 지점(파일 값)을 쓴다
    await saveRallyEvent({}, { points: [], qrTokens: {} });
    await refresh();
    setMsg("↩️ 처음 설정(파일에 적힌 지점)으로 되돌렸어요.");
  }

  return (
    <section className="panel rallypt">
      <header className="panel__head">
        <h2>📍 랠리 지점</h2>
        <p>
          스탬프를 찍는 장소예요. <b>완주 = 여기 있는 지점 전부</b> 모으기.
          {overridden ? " (지금은 여기서 편집한 값을 쓰는 중)" : " (지금은 처음 설정값을 쓰는 중)"}
        </p>
      </header>

      <div className="rallypt__actions">
        <button className="btn" onClick={add} disabled={!loaded}>+ 지점 추가</button>
        {/* 🔴 저장한 뒤가 아니라 **저장하기 전에** 말한다 — 고칠 수 있을 때 말해야 뜻이 있다 */}
        {check.dropped.length || check.changed.length ? (
          <div className="rallypt__warn" role="status">
            {check.dropped.map((d) => (
              <p key={`d${d.id}`}>
                ⚠️ <b>{d.id}</b> — {RALLY_DROP_SAY[d.reason]} <em>(저장 안 돼요)</em>
              </p>
            ))}
            {check.changed.map((c, i) => (
              <p key={`c${c.id}${i}`}>
                ℹ️ <b>{c.id}</b> — {c.what}
              </p>
            ))}
          </div>
        ) : null}
        <ActionButton
          onAction={onReset}
          doneLabel="되돌렸어요"
          className="btn btn--sm"
          confirm="편집한 지점을 버리고 처음 설정으로 되돌릴까요?"
        >
          처음 설정으로
        </ActionButton>
      </div>

      <Msg text={msg} />

      <ul className="rallypt__list">
        {points.map((p, i) => (
          <li key={i} className="rallypt__item">
            <div className="rallypt__row">
              <label className="rallypt__grow">
                장소 이름
                <input
                  value={p.title_ko}
                  placeholder="예: 첨성대"
                  onChange={(e) => update(i, { title_ko: e.target.value })}
                />
              </label>
              <label>
                id(영문)
                <input
                  value={p.id}
                  placeholder="chumseongdae"
                  onChange={(e) => update(i, { id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") })}
                />
              </label>
            </div>
            <div className="rallypt__row">
              <label>
                위도
                <input
                  type="number"
                  step="0.0001"
                  value={p.lat}
                  onChange={(e) => update(i, { lat: Number(e.target.value) })}
                />
              </label>
              <label>
                경도
                <input
                  type="number"
                  step="0.0001"
                  value={p.lng}
                  onChange={(e) => update(i, { lng: Number(e.target.value) })}
                />
              </label>
              <label>
                반경(m)
                <input
                  type="number"
                  min={10}
                  max={500}
                  value={p.radius_m}
                  onChange={(e) => update(i, { radius_m: Number(e.target.value) })}
                />
              </label>
              <label>
                인증 방식
                <select
                  value={p.verify}
                  onChange={(e) => update(i, { verify: e.target.value as RallyPointDoc["verify"] })}
                >
                  <option value="geofence_ar">위치로 인증</option>
                  <option value="onsite_qr">현장 QR + 위치</option>
                </select>
              </label>
            </div>

            {p.verify === "onsite_qr" ? (
              <label className="rallypt__token">
                🔑 현장 QR 비밀 토큰(가게에 붙일 QR 에 들어감 — 외부에 공개되지 않아요)
                <input
                  value={tokens[p.id] ?? ""}
                  placeholder="아무도 못 맞출 긴 문자열"
                  onChange={(e) => setTokens((t) => ({ ...t, [p.id]: e.target.value }))}
                />
              </label>
            ) : null}

            {/**
             * 🖨️ **여기서 바로 인쇄한다.**
             *
             * 토큰만 정해 놓고 인쇄할 방법이 없으면, 운영자는 주소를 손으로 만들어
             * **바깥 QR 만드는 사이트에 비밀 토큰을 붙여 넣게 된다.** 그럴 이유가 없다 —
             * 만드는 도구는 이미 여기 있다.
             *
             * 토큰이 비어 있으면 안 보여 준다: 그 QR 은 찍어도 인증이 안 되고,
             * 종이는 회수할 수 없다.
             */}
            {p.verify === "onsite_qr" && (tokens[p.id] ?? "").trim() ? (
              <QrPrint
                path={`/rally?p=${encodeURIComponent(p.id)}&t=${encodeURIComponent(
                  (tokens[p.id] ?? "").trim(),
                )}`}
                title={`📍 ${p.title_ko || p.id}`}
                caption="여기서 카메라로 찍으면 도장을 받아요"
                hideUrl
              />
            ) : null}
            {p.verify === "onsite_qr" && !(tokens[p.id] ?? "").trim() ? (
              <p className="rallypt__hint">
                🔑 비밀 토큰을 넣으면 <b>[🖨️ QR 인쇄]</b>가 여기 떠요. 토큰 없이 찍은 QR 은
                찍어도 도장이 안 돼요 — 종이는 다시 못 바꿔요.
              </p>
            ) : null}

            <div className="rallypt__foot">
              <a
                className="btn btn--sm"
                href={`https://map.kakao.com/link/map/${encodeURIComponent(p.title_ko || "위치")},${p.lat},${p.lng}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                지도에서 확인 ↗
              </a>
              <button className="btn btn--sm" onClick={() => remove(i)}>삭제</button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** 중복 없는 새 id. */
function nextId(list: RallyPointDoc[]): string {
  const used = new Set(list.map((p) => p.id));
  let i = 1;
  let id = `spot_${i}`;
  while (used.has(id)) id = `spot_${++i}`;
  return id;
}
